(() => {
  const node = document.querySelector('[data-neo-market-stock-config]');
  if (!node) return;

  let cfg;
  try { cfg = JSON.parse(node.textContent || '{}'); } catch (e) { return; }

  const norm = v => String(v ?? '').trim().toLowerCase();
  const numId = v => String(v ?? '').split('/').pop().replace(/\D/g, '');
  const list = v => String(v || '').split(/[\n,]/).map(norm).filter(Boolean);
  const market = norm(cfg.marketHandle);
  const country = norm(cfg.countryCode);
  const rules = (cfg.rules || []).map(r => ({
    ...r,
    productId: numId(r.productId),
    productHandle: norm(r.productHandle),
    marketHandles: list(r.marketHandles),
    countryCodes: list(r.countryCodes),
    variantIds: list(r.variantIds).map(numId),
    variantValues: list(r.variantValues)
  }));

  const itemValues = item => {
    const values = [];
    if (Array.isArray(item?.options)) values.push(...item.options);
    if (Array.isArray(item?.options_with_values)) values.push(...item.options_with_values.map(o => o?.value ?? o));
    if (item?.variant_title) values.push(item.variant_title);
    return values.map(norm);
  };

  const matchingRule = item => {
    if (cfg.enabled === false) return null;
    const pid = numId(item?.product_id ?? item?.productId);
    const handle = norm(item?.handle ?? item?.product_handle ?? item?.productHandle);
    const vid = numId(item?.variant_id ?? item?.variantId ?? item?.id);
    const values = itemValues(item);

    return rules.find(r => {
      if (r.enabled === false) return false;
      const marketMatch = (!r.marketHandles.length && !r.countryCodes.length) ||
        r.marketHandles.includes(market) || r.countryCodes.includes(country);
      const productMatch = (r.productId && pid && r.productId === pid) ||
        (r.productHandle && handle && r.productHandle === handle);
      const variantMatch = (!r.variantIds.length && !r.variantValues.length) ||
        (vid && r.variantIds.includes(vid)) || r.variantValues.some(v => values.includes(v));
      return marketMatch && productMatch && variantMatch;
    }) || null;
  };

  const message = r => String(r?.message || cfg.defaultMessage || 'This variant is unavailable in your selected market.');
  const productHandle = () => norm((location.pathname.match(/\/products\/([^/?#]+)/i) || [])[1]);
  const asItem = v => ({
    id: v.id, variant_id: v.id, product_id: v.product_id, handle: productHandle(),
    variant_title: v.title || v.name, options: Array.isArray(v.options) ? v.options : []
  });

  window.NeoMarketStock = { config: cfg, matchingRule, isBlocked: item => !!matchingRule(item), refresh: boot };

  const parseVariants = root => {
    try { return JSON.parse(root.querySelector('[data-neo-variants]')?.textContent || '[]'); }
    catch (e) { return []; }
  };
  const selectedOptions = root => [...root.querySelectorAll('[data-neo-option]')].map(g => g.querySelector('[data-neo-optinput]:checked')?.value);
  const findVariant = (variants, options) => variants.find(v => Array.isArray(v.options) && v.options.every((x, i) => x === options[i]));
  const selectedVariant = (root, variants) => {
    const id = numId(root.querySelector('[data-neo-id]')?.value);
    return variants.find(v => numId(v.id) === id) || findVariant(variants, selectedOptions(root));
  };
  const sellable = v => !!(v?.available && !matchingRule(asItem(v)));

  function syncSingleValueOptions(root, variants) {
    const enabled = root.dataset.hideSingleValueOptions === 'true';
    const singleValueOptionNames = list(root.dataset.singleValueOptionNames);
    const groups = [...root.querySelectorAll('[data-neo-option]')];
    groups.forEach(group => {
      const optionName = norm(group.dataset.optionName);
      const isTarget = !singleValueOptionNames.length || singleValueOptionNames.includes(optionName);
      if (!enabled || !isTarget) {
        group.hidden = false;
        group.removeAttribute('aria-hidden');
        return;
      }

      const pos = Math.max(0, (parseInt(group.dataset.position, 10) || 1) - 1);
      const values = new Set(
        variants
          .filter(sellable)
          .map(variant => variant.options?.[pos])
          .filter(value => value != null && value !== '')
      );
      const shouldHide = values.size === 1;
      group.hidden = shouldHide;
      if (shouldHide) group.setAttribute('aria-hidden', 'true');
      else group.removeAttribute('aria-hidden');
    });
  }

  function selectVariant(root, variant) {
    if (!variant || root.dataset.neoMarketSwitching) return;
    root.dataset.neoMarketSwitching = '1';
    let changed;
    [...root.querySelectorAll('[data-neo-option]')].forEach((group, i) => {
      const input = [...group.querySelectorAll('[data-neo-optinput]')].find(x => x.value === variant.options[i]);
      if (input) { input.disabled = false; input.checked = true; changed = input; }
    });
    changed?.dispatchEvent(new Event('change', { bubbles: true }));
    setTimeout(() => { delete root.dataset.neoMarketSwitching; applyHero(root); }, 0);
  }

  function applyHero(root) {
    const variants = parseVariants(root);
    if (!variants.length) return;
    const selected = selectedOptions(root);

    [...root.querySelectorAll('[data-neo-option]')].forEach(group => {
      const pos = Math.max(0, (parseInt(group.dataset.position, 10) || 1) - 1);
      group.querySelectorAll('[data-neo-optinput]').forEach(input => {
        const candidate = [...selected]; candidate[pos] = input.value;
        const variant = findVariant(variants, candidate);
        const rule = variant ? matchingRule(asItem(variant)) : null;
        const disabled = !variant?.available || !!rule;
        input.disabled = disabled;
        input.classList.toggle('is-unavailable', disabled);
        input.toggleAttribute('aria-disabled', disabled);
        const label = input.closest('label');
        label?.classList.toggle('neo-market-stock__option-restricted', disabled);
        if (rule) label?.setAttribute('title', message(rule)); else label?.removeAttribute('title');
      });
    });

    syncSingleValueOptions(root, variants);

    let variant = selectedVariant(root, variants);
    let rule = variant ? matchingRule(asItem(variant)) : null;
    if ((!variant?.available || rule) && cfg.autoSelectAvailable !== false && !root.dataset.neoMarketSwitching) {
      const replacement = variants.find(sellable);
      if (replacement && numId(replacement.id) !== numId(variant.id)) return selectVariant(root, replacement);
    }

    const blocked = !!rule;
    const canSell = !!(variant?.available && !blocked);
    const atc = root.querySelector('[data-neo-atc]');
    const sticky = root.querySelector('[data-neo-sticky-btn]');
    const label = root.querySelector('[data-neo-atc-label]');
    if (atc) atc.disabled = !canSell;
    if (sticky) sticky.disabled = !canSell;
    if (blocked && label) label.textContent = cfg.soldOutLabel || 'Sold out in your market';
    const payment = root.querySelector('.shopify-payment-button');
    if (payment) payment.hidden = !canSell;

    const stock = root.querySelector('.neo-buy__stock');
    if (stock) {
      stock.dataset.neoOriginal ||= stock.innerHTML;
      stock.classList.toggle('neo-market-stock__restricted', blocked);
      stock.innerHTML = blocked ? `<span class="neo-buy__stockdot" aria-hidden="true"></span>${message(rule)}` : stock.dataset.neoOriginal;
    }
  }

  function initHeroes() {
    document.querySelectorAll('[data-neo-hero]').forEach(root => {
      if (!root.dataset.neoMarketBound) {
        root.dataset.neoMarketBound = '1';
        root.addEventListener('change', () => setTimeout(() => applyHero(root), 0));
      }
      applyHero(root);
    });
  }

  function itemFromPage(variantId) {
    for (const root of document.querySelectorAll('[data-neo-hero]')) {
      const v = parseVariants(root).find(x => numId(x.id) === numId(variantId));
      if (v) return asItem(v);
    }
    const r = rules.find(x => x.enabled !== false && x.variantIds.includes(numId(variantId)));
    return r ? { variant_id: variantId, product_id: r.productId, handle: r.productHandle } : null;
  }

  document.addEventListener('submit', e => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if ((form.getAttribute('action') || '').includes('/cart/add')) {
      const item = itemFromPage(form.querySelector('[name="id"]')?.value);
      const rule = item && matchingRule(item);
      if (!rule) return;
      e.preventDefault(); e.stopImmediatePropagation();
      let error = form.querySelector('.neo-market-stock__form-error');
      if (!error) { error = document.createElement('div'); error.className = 'neo-market-stock__form-error'; error.setAttribute('role', 'alert'); form.append(error); }
      error.textContent = message(rule);
    }
  }, true);

  let cartBlocked = false, cartTimer;
  const cartUrl = () => ((window.routes?.cart_url || '/cart').replace(/\.js(?:\?.*)?$/, '') + '.js');
  const scheduleCart = () => { clearTimeout(cartTimer); cartTimer = setTimeout(checkCart, 100); };

  function setNotice(container, show) {
    if (!container) return;
    let notice = container.querySelector(':scope > .neo-market-stock__notice');
    if (!show) { notice?.remove(); return; }
    if (!notice) { notice = document.createElement('div'); notice.className = 'neo-market-stock__notice'; notice.setAttribute('role', 'alert'); notice.tabIndex = -1; container.prepend(notice); }
    notice.textContent = cfg.cartMessage || 'One or more items are unavailable in your selected market.';
  }

  function updateCart(blockedItems) {
    cartBlocked = blockedItems.length > 0 && cfg.guardCartCheckout !== false;
    document.querySelectorAll('button[name="checkout"],input[name="checkout"],#checkout,#CartDrawer-Checkout').forEach(b => {
      b.disabled = cartBlocked; b.classList.toggle('neo-market-stock__disabled-checkout', cartBlocked);
    });
    document.querySelectorAll('.cart__dynamic-checkout-buttons,.additional-checkout-buttons').forEach(x => x.hidden = cartBlocked);
    document.querySelectorAll('.cart__blocks,.drawer__footer').forEach(x => setNotice(x, cartBlocked));
    document.querySelectorAll('.cart-item').forEach(row => { row.classList.remove('neo-market-stock__restricted'); row.querySelector('.neo-market-stock__badge')?.remove(); });
    blockedItems.forEach(({ item, rule }) => {
      document.querySelectorAll(`[data-quantity-variant-id="${CSS.escape(String(item.variant_id))}"]`).forEach(input => {
        const row = input.closest('.cart-item'); if (!row) return;
        row.classList.add('neo-market-stock__restricted');
        if (cfg.showCartItemBadge !== false && !row.querySelector('.neo-market-stock__badge')) {
          const badge = document.createElement('span'); badge.className = 'neo-market-stock__badge'; badge.textContent = message(rule);
          row.querySelector('.cart-item__details')?.append(badge);
        }
      });
    });
  }

  async function checkCart() {
    if (cfg.enabled === false || !document.querySelector('cart-drawer,cart-items,#main-cart-footer,[name="checkout"]')) return;
    try {
      const cart = await fetch(cartUrl(), { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(r => r.json());
      updateCart((cart.items || []).map(item => ({ item, rule: matchingRule(item) })).filter(x => x.rule));
    } catch (e) { console.warn('Neo market stock cart check failed.', e); }
  }

  document.addEventListener('click', e => {
    const checkout = e.target.closest?.('button[name="checkout"],input[name="checkout"],#checkout,#CartDrawer-Checkout');
    if (!checkout || !cartBlocked) return;
    e.preventDefault(); e.stopImmediatePropagation();
    document.querySelector('.neo-market-stock__notice')?.focus();
  }, true);

  function boot() { initHeroes(); scheduleCart(); }
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot, { once: true }) : boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('neo:market-stock-refresh', boot);
  new MutationObserver(ms => ms.some(m => [...m.addedNodes].some(n => n.nodeType === 1 && (n.matches?.('.cart-item,cart-drawer-items,.drawer__footer') || n.querySelector?.('.cart-item')))) && scheduleCart())
    .observe(document.documentElement, { childList: true, subtree: true });
})();
